import { createAsyncThunk } from '@reduxjs/toolkit';
import { ebookApi } from './api';
import { UserState } from '@features/Auth/user.slice';

export interface UserResponse {
  signedIn: boolean;
  id: number;
  ebookId: number;
  name: string;
  country: {
    name: string;
    isocode: string;
    timezone: string;
  };
  station: null;
  username: string;
  password: null;
  permissions: {
    selectReturnLocation: boolean;
    locationMap: boolean;
    showOnlyCarsForOpenLocations: boolean;
    showOnlyCarsWithKeyboxcode: boolean;
    mapSelectCarReservation: boolean;
    mapSelectCarAgreement: boolean;
    showPriceTable: boolean;
    ignoreOpeningHours: boolean;
    sendAgreementExtraConfirmation: boolean;
    manualPayerInput: boolean;
    manualReturnLocationAddressAllowed: boolean;
    manualPickupLocationAddressAllowed: boolean;
    advancedCheckin: boolean;
    showEmailField: boolean;
    showCheckoutMobileNumberField: boolean;
    showReservationMobileNumberField: boolean;
    allowSelectChargedCarGroup: boolean;
    cdpInput: boolean;
    hccInput: boolean;
    tariffInput: boolean;
    voucherAccountNumberInput: boolean;
    voucherAmountInput: boolean;
    voucherReferenceInput: boolean;
    voucherNumberInput: boolean;
    licenseNumberInput: boolean;
    pciCreditCardInput: boolean;
    equipment: boolean;
    reservationToAgreement: boolean;
    carSearch: boolean;
    driverInsurance: boolean;
    checkoutCarFirst: boolean;
    showEstimatedPrice: boolean;
    reservationPayerRequired: boolean;
    workshopAnalysis: boolean;
    showDriverLicenseInputField: boolean;
    setForceDriverLicenseExpireCheckout: boolean;
    forceDriverLicenseFieldsCheckout: boolean;
    forceDriverBirthdateFieldCheckout: boolean;
    dynamicTimeCarBooking: boolean;
    ordererEmail: boolean;
    bisnodeSearchCustomer: boolean;
    bisnodeSearchPhoneAndZipCode: boolean;
    bisnodeSearchSocialSecurityNumber: boolean;
    bisnodeSearchRegnr: boolean;
    onlyEmailCheckin: boolean;
    invoiceTextFromPayer: boolean;
    allowCheckinBeforeToday: boolean;
    locationAdmin: boolean;
    payerAdmin: boolean;
    locationUser: boolean;
    connections: boolean;
    reservationMobileNumberRequired: boolean;
    reservationEmailAddressRequired: boolean;
    checkoutEmailAddressRequired: boolean;
    guaranteedCarDelivery: boolean;
    showManualPickupPlaceDeafultText: boolean;
    showRentalAgreementTextToCheckin: boolean;
    canDuplicateReservation: boolean;
    ccByOcOnly: boolean;
    sendPaymentLink: boolean;
    checkoutEmailMandatory: boolean;
    regNumberMandatoryForManualPayer: boolean;
    regNumberMandatoryForRegularPayer: boolean;
    reservationEmailMandatory: boolean;
    reservationMobileNumberMandatory: boolean;
    forceDriverLicenseExpireCheckout: boolean;
  };
  reservationEarliestInHours: number;
  roles: [];
  firstName: null;
  lastName: null;
  email: null;
  telephone: null;
  mobileNumber: null;
  company: string;
  companyAddress: null;
  companyPostcode: null;
  companyCity: null;
  companyTelephone: null;
  companyContactPerson: null;
  socialSecurityNumber: null;
  imageUrl: string;
  facebookUrl: null;
  blogUrl: null;
  city: null;
  twitterUrl: null;
  pickupPlaceStation: null;
  pickupPlaceId: null;
  rightsHash: string;
  sign: null;
  reservationDaysOffset: number;
  reservationDefaultTime: null;
  useNewLocations: boolean;
  locationId: null;
  connectionsPayerId: null;
  logoPath: null;
  locationAdmin: boolean;
  priceListFilename: null;
  accountActive: boolean;
  findStationsFromPickupPlaces: boolean;
  info: null;
  locale: {
    lang: string;
    country: string;
  };
}

export interface LoginRequest {
  username: string;
  password: string;
}

export const fetchInitialUserData = createAsyncThunk<UserResponse, void>(
  'user/fetchInitialUserData',
  async () => {
    const response = await fetch('/api/users');
    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }
    const data = await response.json();
    return data;
  }
);

const authApi = ebookApi.injectEndpoints({
  endpoints: (build) => ({
    updateUser: build.mutation<void, UserState>({
      query: (userData) => ({
        url: 'users',
        method: 'POST',
        body: userData.user,
      }),
    }),
    login: build.mutation<UserResponse, LoginRequest>({
      query: (credentials) => ({
        url: 'users',
        method: 'PUT',
        body: credentials,
        providesTags: ['User'],
      }),
    }),
    logout: build.mutation<{ success: boolean }, object>({
      query: () => ({
        url: 'users',
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    getUser: build.query<UserResponse, void>({
      query: () => '/users',
      providesTags: ['User'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useUpdateUserMutation,
} = authApi;
